/**
 * 轻量级 QR Code 生成器 (适用于 uni-app)
 * 基于 QRCode Generator (MIT License) by Kazuhiko Arase
 * 精简版 - 支持 Byte 模式编码，适合钱包地址等文本内容
 */

const QRMode = { MODE_NUMBER: 1, MODE_ALPHA_NUM: 2, MODE_8BIT_BYTE: 4, MODE_KANJI: 8 }
const QRErrorCorrectLevel = { L: 1, M: 0, Q: 3, H: 2 }

const QRMaskPattern = {
	PATTERN000: 0, PATTERN001: 1, PATTERN010: 2, PATTERN011: 3,
	PATTERN100: 4, PATTERN101: 5, PATTERN110: 6, PATTERN111: 7
}

const QRUtil = {
	PATTERN_POSITION_TABLE: [
		[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34],
		[6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50], [6, 30, 54],
		[6, 32, 58], [6, 34, 62], [6, 26, 46, 66], [6, 26, 48, 70],
		[6, 26, 50, 74], [6, 30, 58, 86], [6, 28, 54, 80], [6, 32, 58, 84],
		[6, 26, 48, 70, 92], [6, 30, 56, 82, 108], [6, 28, 52, 76, 100],
		[6, 32, 60, 88, 116], [6, 26, 50, 74, 98], [6, 30, 54, 78, 102],
		[6, 28, 54, 80, 106], [6, 32, 58, 84, 110], [6, 30, 56, 82, 108],
		[6, 34, 62, 90, 118], [6, 26, 50, 74, 98, 122], [6, 30, 54, 78, 102, 126],
		[6, 26, 52, 78, 104, 130], [6, 30, 56, 82, 108, 134],
		[6, 34, 60, 86, 112, 138], [6, 30, 58, 86, 114, 142],
		[6, 34, 62, 90, 118, 146], [6, 30, 54, 78, 102, 126, 150],
		[6, 24, 50, 76, 102, 128, 154], [6, 28, 54, 80, 106, 132, 158],
		[6, 32, 58, 84, 110, 136, 162], [6, 26, 54, 82, 110, 138, 166]
	],
	G15: (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0),
	G18: (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0),
	G15_MASK: (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1),

	getBCHTypeInfo(data) {
		let d = data << 10
		while (this._getBCHDigit(d) - this._getBCHDigit(this.G15) >= 0)
			d ^= (this.G15 << (this._getBCHDigit(d) - this._getBCHDigit(this.G15)))
		return ((data << 10) | d) ^ this.G15_MASK
	},
	getBCHTypeNumber(data) {
		let d = data << 12
		while (this._getBCHDigit(d) - this._getBCHDigit(this.G18) >= 0)
			d ^= (this.G18 << (this._getBCHDigit(d) - this._getBCHDigit(this.G18)))
		return (data << 12) | d
	},
	_getBCHDigit(data) {
		let digit = 0
		while (data !== 0) { digit++; data >>>= 1 }
		return digit
	},
	getPatternPosition(typeNumber) {
		return this.PATTERN_POSITION_TABLE[typeNumber - 1]
	},
	getMask(maskPattern, i, j) {
		switch (maskPattern) {
			case QRMaskPattern.PATTERN000: return (i + j) % 2 === 0
			case QRMaskPattern.PATTERN001: return i % 2 === 0
			case QRMaskPattern.PATTERN010: return j % 3 === 0
			case QRMaskPattern.PATTERN011: return (i + j) % 3 === 0
			case QRMaskPattern.PATTERN100: return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 === 0
			case QRMaskPattern.PATTERN101: return (i * j) % 2 + (i * j) % 3 === 0
			case QRMaskPattern.PATTERN110: return ((i * j) % 2 + (i * j) % 3) % 2 === 0
			case QRMaskPattern.PATTERN111: return ((i * j) % 3 + (i + j) % 2) % 2 === 0
			default: throw new Error('bad maskPattern:' + maskPattern)
		}
	},
	getErrorCorrectPolynomial(errorCorrectLength) {
		let a = new QRPolynomial([1], 0)
		for (let i = 0; i < errorCorrectLength; i++)
			a = a.multiply(new QRPolynomial([1, QRMath.gexp(i)], 0))
		return a
	},
	getLengthInBits(mode, type) {
		if (1 <= type && type < 10) {
			switch (mode) {
				case QRMode.MODE_NUMBER: return 10
				case QRMode.MODE_ALPHA_NUM: return 9
				case QRMode.MODE_8BIT_BYTE: return 8
				case QRMode.MODE_KANJI: return 8
				default: throw new Error('mode:' + mode)
			}
		} else if (type < 27) {
			switch (mode) {
				case QRMode.MODE_NUMBER: return 12
				case QRMode.MODE_ALPHA_NUM: return 11
				case QRMode.MODE_8BIT_BYTE: return 16
				case QRMode.MODE_KANJI: return 10
				default: throw new Error('mode:' + mode)
			}
		} else if (type < 41) {
			switch (mode) {
				case QRMode.MODE_NUMBER: return 14
				case QRMode.MODE_ALPHA_NUM: return 13
				case QRMode.MODE_8BIT_BYTE: return 16
				case QRMode.MODE_KANJI: return 12
				default: throw new Error('mode:' + mode)
			}
		} else {
			throw new Error('type:' + type)
		}
	},
	getLostPoint(qrCode) {
		const moduleCount = qrCode.getModuleCount()
		let lostPoint = 0
		for (let row = 0; row < moduleCount; row++) {
			for (let col = 0; col < moduleCount; col++) {
				let sameCount = 0
				const dark = qrCode.isDark(row, col)
				for (let r = -1; r <= 1; r++) {
					if (row + r < 0 || moduleCount <= row + r) continue
					for (let c = -1; c <= 1; c++) {
						if (col + c < 0 || moduleCount <= col + c) continue
						if (r === 0 && c === 0) continue
						if (dark === qrCode.isDark(row + r, col + c)) sameCount++
					}
				}
				if (sameCount > 5) lostPoint += (3 + sameCount - 5)
			}
		}
		for (let row = 0; row < moduleCount - 1; row++) {
			for (let col = 0; col < moduleCount - 1; col++) {
				let count = 0
				if (qrCode.isDark(row, col)) count++
				if (qrCode.isDark(row + 1, col)) count++
				if (qrCode.isDark(row, col + 1)) count++
				if (qrCode.isDark(row + 1, col + 1)) count++
				if (count === 0 || count === 4) lostPoint += 3
			}
		}
		for (let row = 0; row < moduleCount; row++) {
			for (let col = 0; col < moduleCount - 6; col++) {
				if (qrCode.isDark(row, col) && !qrCode.isDark(row, col + 1) && qrCode.isDark(row, col + 2) && qrCode.isDark(row, col + 3) && qrCode.isDark(row, col + 4) && !qrCode.isDark(row, col + 5) && qrCode.isDark(row, col + 6))
					lostPoint += 40
			}
		}
		for (let col = 0; col < moduleCount; col++) {
			for (let row = 0; row < moduleCount - 6; row++) {
				if (qrCode.isDark(row, col) && !qrCode.isDark(row + 1, col) && qrCode.isDark(row + 2, col) && qrCode.isDark(row + 3, col) && qrCode.isDark(row + 4, col) && !qrCode.isDark(row + 5, col) && qrCode.isDark(row + 6, col))
					lostPoint += 40
			}
		}
		let darkCount = 0
		for (let col = 0; col < moduleCount; col++) {
			for (let row = 0; row < moduleCount; row++) {
				if (qrCode.isDark(row, col)) darkCount++
			}
		}
		const ratio = Math.abs(100 * darkCount / moduleCount / moduleCount - 50) / 5
		lostPoint += ratio * 10
		return lostPoint
	}
}

const QRMath = {
	_EXP_TABLE: new Array(256),
	_LOG_TABLE: new Array(256),
	glog(n) {
		if (n < 1) throw new Error('glog(' + n + ')')
		return this._LOG_TABLE[n]
	},
	gexp(n) {
		while (n < 0) n += 255
		while (n >= 256) n -= 255
		return this._EXP_TABLE[n]
	}
}

// init tables
;(function () {
	for (let i = 0; i < 8; i++) QRMath._EXP_TABLE[i] = 1 << i
	for (let i = 8; i < 256; i++) QRMath._EXP_TABLE[i] = QRMath._EXP_TABLE[i - 4] ^ QRMath._EXP_TABLE[i - 5] ^ QRMath._EXP_TABLE[i - 6] ^ QRMath._EXP_TABLE[i - 8]
	for (let i = 0; i < 255; i++) QRMath._LOG_TABLE[QRMath._EXP_TABLE[i]] = i
})()

class QRPolynomial {
	constructor(num, shift) {
		if (num.length === undefined) throw new Error(num.length + '/' + shift)
		let offset = 0
		while (offset < num.length && num[offset] === 0) offset++
		this._num = new Array(num.length - offset + shift)
		for (let i = 0; i < num.length - offset; i++) this._num[i] = num[i + offset]
	}
	get(index) { return this._num[index] }
	getLength() { return this._num.length }
	multiply(e) {
		const num = new Array(this.getLength() + e.getLength() - 1)
		for (let i = 0; i < this.getLength(); i++)
			for (let j = 0; j < e.getLength(); j++)
				num[i + j] ^= QRMath.gexp(QRMath.glog(this.get(i)) + QRMath.glog(e.get(j)))
		return new QRPolynomial(num, 0)
	}
	mod(e) {
		if (this.getLength() - e.getLength() < 0) return this
		const ratio = QRMath.glog(this.get(0)) - QRMath.glog(e.get(0))
		const num = new Array(this.getLength())
		for (let i = 0; i < this.getLength(); i++) num[i] = this.get(i)
		for (let i = 0; i < e.getLength(); i++) num[i] ^= QRMath.gexp(QRMath.glog(e.get(i)) + ratio)
		return new QRPolynomial(num, 0).mod(e)
	}
}

class QR8bitByte {
	constructor(data) {
		this.mode = QRMode.MODE_8BIT_BYTE
		this.data = data
	}
	getLength() { return this.data.length }
	write(buffer) {
		for (let i = 0; i < this.data.length; i++) buffer.put(this.data.charCodeAt(i), 8)
	}
}

class QRBitBuffer {
	constructor() { this.buffer = []; this.length = 0 }
	get(index) { return ((this.buffer[Math.floor(index / 8)] >>> (7 - index % 8)) & 1) === 1 }
	put(num, length) { for (let i = 0; i < length; i++) this.putBit(((num >>> (length - i - 1)) & 1) === 1) }
	getLengthInBits() { return this.length }
	putBit(bit) {
		const bufIndex = Math.floor(this.length / 8)
		if (this.buffer.length <= bufIndex) this.buffer.push(0)
		if (bit) this.buffer[bufIndex] |= (0x80 >>> (this.length % 8))
		this.length++
	}
}

// RS Block 参数表
const RS_BLOCK_TABLE = [
	[1, 26, 19], [1, 26, 16], [1, 26, 13], [1, 26, 9],
	[1, 44, 34], [1, 44, 28], [1, 44, 22], [1, 44, 16],
	[1, 70, 55], [1, 70, 44], [2, 35, 17], [2, 35, 13],
	[1, 100, 80], [2, 50, 32], [2, 50, 24], [4, 25, 9],
	[1, 134, 108], [2, 67, 43], [2, 33, 15, 2, 34, 16], [2, 33, 11, 2, 34, 12],
	[2, 86, 68], [4, 43, 27], [4, 43, 19], [4, 43, 15],
	[2, 98, 78], [4, 49, 31], [2, 32, 14, 4, 33, 15], [4, 39, 13, 1, 40, 14],
	[2, 121, 97], [2, 60, 38, 2, 61, 39], [4, 40, 18, 2, 41, 19], [4, 40, 14, 2, 41, 15],
	[2, 146, 116], [3, 58, 36, 2, 59, 37], [4, 36, 16, 4, 37, 17], [4, 36, 12, 4, 37, 13],
	[2, 86, 68, 2, 87, 69], [4, 69, 43, 1, 70, 44], [6, 43, 19, 2, 44, 20], [6, 43, 15, 2, 44, 16],
]

function getRsBlockTable(typeNumber, errorCorrectLevel) {
	switch (errorCorrectLevel) {
		case QRErrorCorrectLevel.L: return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 0]
		case QRErrorCorrectLevel.M: return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 1]
		case QRErrorCorrectLevel.Q: return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 2]
		case QRErrorCorrectLevel.H: return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 3]
		default: return undefined
	}
}

function getRsBlocks(typeNumber, errorCorrectLevel) {
	const rsBlock = getRsBlockTable(typeNumber, errorCorrectLevel)
	if (!rsBlock) throw new Error('bad rs block @ typeNumber:' + typeNumber + '/errorCorrectLevel:' + errorCorrectLevel)
	const list = []
	for (let i = 0; i < rsBlock.length; i += 3) {
		const count = rsBlock[i], totalCount = rsBlock[i + 1], dataCount = rsBlock[i + 2]
		for (let j = 0; j < count; j++) list.push({ totalCount, dataCount })
	}
	return list
}

class QRCodeModel {
	constructor(typeNumber, errorCorrectLevel) {
		this.typeNumber = typeNumber
		this.errorCorrectLevel = errorCorrectLevel
		this.modules = null
		this.moduleCount = 0
		this.dataCache = null
		this.dataList = []
	}

	addData(data) { this.dataList.push(new QR8bitByte(data)); this.dataCache = null }

	isDark(row, col) {
		if (row < 0 || this.moduleCount <= row || col < 0 || this.moduleCount <= col)
			throw new Error(row + ',' + col)
		return this.modules[row][col]
	}

	getModuleCount() { return this.moduleCount }

	make() { this._makeImpl(false, this._getBestMaskPattern()) }

	_makeImpl(test, maskPattern) {
		this.moduleCount = this.typeNumber * 4 + 17
		this.modules = new Array(this.moduleCount)
		for (let row = 0; row < this.moduleCount; row++) {
			this.modules[row] = new Array(this.moduleCount)
			for (let col = 0; col < this.moduleCount; col++) this.modules[row][col] = null
		}
		this._setupPositionProbePattern(0, 0)
		this._setupPositionProbePattern(this.moduleCount - 7, 0)
		this._setupPositionProbePattern(0, this.moduleCount - 7)
		this._setupPositionAdjustPattern()
		this._setupTimingPattern()
		this._setupTypeInfo(test, maskPattern)
		if (this.typeNumber >= 7) this._setupTypeNumber(test)
		if (this.dataCache === null)
			this.dataCache = QRCodeModel._createData(this.typeNumber, this.errorCorrectLevel, this.dataList)
		this._mapData(this.dataCache, maskPattern)
	}

	_setupPositionProbePattern(row, col) {
		for (let r = -1; r <= 7; r++) {
			if (row + r <= -1 || this.moduleCount <= row + r) continue
			for (let c = -1; c <= 7; c++) {
				if (col + c <= -1 || this.moduleCount <= col + c) continue
				if ((0 <= r && r <= 6 && (c === 0 || c === 6)) || (0 <= c && c <= 6 && (r === 0 || r === 6)) || (2 <= r && r <= 4 && 2 <= c && c <= 4))
					this.modules[row + r][col + c] = true
				else
					this.modules[row + r][col + c] = false
			}
		}
	}

	_getBestMaskPattern() {
		let minLostPoint = 0, pattern = 0
		for (let i = 0; i < 8; i++) {
			this._makeImpl(true, i)
			const lostPoint = QRUtil.getLostPoint(this)
			if (i === 0 || minLostPoint > lostPoint) { minLostPoint = lostPoint; pattern = i }
		}
		return pattern
	}

	_setupTimingPattern() {
		for (let r = 8; r < this.moduleCount - 8; r++) {
			if (this.modules[r][6] !== null) continue
			this.modules[r][6] = (r % 2 === 0)
		}
		for (let c = 8; c < this.moduleCount - 8; c++) {
			if (this.modules[6][c] !== null) continue
			this.modules[6][c] = (c % 2 === 0)
		}
	}

	_setupPositionAdjustPattern() {
		const pos = QRUtil.getPatternPosition(this.typeNumber)
		for (let i = 0; i < pos.length; i++) {
			for (let j = 0; j < pos.length; j++) {
				const row = pos[i], col = pos[j]
				if (this.modules[row][col] !== null) continue
				for (let r = -2; r <= 2; r++) {
					for (let c = -2; c <= 2; c++) {
						if (r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0))
							this.modules[row + r][col + c] = true
						else
							this.modules[row + r][col + c] = false
					}
				}
			}
		}
	}

	_setupTypeNumber(test) {
		const bits = QRUtil.getBCHTypeNumber(this.typeNumber)
		for (let i = 0; i < 18; i++) {
			const mod = (!test && ((bits >> i) & 1) === 1)
			this.modules[Math.floor(i / 3)][i % 3 + this.moduleCount - 8 - 3] = mod
		}
		for (let i = 0; i < 18; i++) {
			const mod = (!test && ((bits >> i) & 1) === 1)
			this.modules[i % 3 + this.moduleCount - 8 - 3][Math.floor(i / 3)] = mod
		}
	}

	_setupTypeInfo(test, maskPattern) {
		const data = (this.errorCorrectLevel << 3) | maskPattern
		const bits = QRUtil.getBCHTypeInfo(data)
		for (let i = 0; i < 15; i++) {
			const mod = (!test && ((bits >> i) & 1) === 1)
			if (i < 6) this.modules[i][8] = mod
			else if (i < 8) this.modules[i + 1][8] = mod
			else this.modules[this.moduleCount - 15 + i][8] = mod
		}
		for (let i = 0; i < 15; i++) {
			const mod = (!test && ((bits >> i) & 1) === 1)
			if (i < 8) this.modules[8][this.moduleCount - i - 1] = mod
			else if (i < 9) this.modules[8][15 - i - 1 + 1] = mod
			else this.modules[8][15 - i - 1] = mod
		}
		this.modules[this.moduleCount - 8][8] = (!test)
	}

	_mapData(data, maskPattern) {
		let inc = -1, row = this.moduleCount - 1, bitIndex = 7, byteIndex = 0
		for (let col = this.moduleCount - 1; col > 0; col -= 2) {
			if (col === 6) col--
			while (true) {
				for (let c = 0; c < 2; c++) {
					if (this.modules[row][col - c] === null) {
						let dark = false
						if (byteIndex < data.length) dark = (((data[byteIndex] >>> bitIndex) & 1) === 1)
						const mask = QRUtil.getMask(maskPattern, row, col - c)
						if (mask) dark = !dark
						this.modules[row][col - c] = dark
						bitIndex--
						if (bitIndex === -1) { byteIndex++; bitIndex = 7 }
					}
				}
				row += inc
				if (row < 0 || this.moduleCount <= row) { row -= inc; inc = -inc; break }
			}
		}
	}

	static _createData(typeNumber, errorCorrectLevel, dataList) {
		const rsBlocks = getRsBlocks(typeNumber, errorCorrectLevel)
		const buffer = new QRBitBuffer()
		for (let i = 0; i < dataList.length; i++) {
			const data = dataList[i]
			buffer.put(data.mode, 4)
			buffer.put(data.getLength(), QRUtil.getLengthInBits(data.mode, typeNumber))
			data.write(buffer)
		}
		let totalDataCount = 0
		for (let i = 0; i < rsBlocks.length; i++) totalDataCount += rsBlocks[i].dataCount
		if (buffer.getLengthInBits() > totalDataCount * 8)
			throw new Error('code length overflow. (' + buffer.getLengthInBits() + '>' + totalDataCount * 8 + ')')
		if (buffer.getLengthInBits() + 4 <= totalDataCount * 8)
			buffer.put(0, 4)
		while (buffer.getLengthInBits() % 8 !== 0) buffer.putBit(false)
		while (true) {
			if (buffer.getLengthInBits() >= totalDataCount * 8) break
			buffer.put(0xEC, 8)
			if (buffer.getLengthInBits() >= totalDataCount * 8) break
			buffer.put(0x11, 8)
		}
		return QRCodeModel._createBytes(buffer, rsBlocks)
	}

	static _createBytes(buffer, rsBlocks) {
		let offset = 0, maxDcCount = 0, maxEcCount = 0
		const dcdata = new Array(rsBlocks.length), ecdata = new Array(rsBlocks.length)
		for (let r = 0; r < rsBlocks.length; r++) {
			const dcCount = rsBlocks[r].dataCount
			const ecCount = rsBlocks[r].totalCount - dcCount
			maxDcCount = Math.max(maxDcCount, dcCount)
			maxEcCount = Math.max(maxEcCount, ecCount)
			dcdata[r] = new Array(dcCount)
			for (let i = 0; i < dcdata[r].length; i++) dcdata[r][i] = 0xff & buffer.buffer[i + offset]
			offset += dcCount
			const rsPoly = QRUtil.getErrorCorrectPolynomial(ecCount)
			const rawPoly = new QRPolynomial(dcdata[r], rsPoly.getLength() - 1)
			const modPoly = rawPoly.mod(rsPoly)
			ecdata[r] = new Array(rsPoly.getLength() - 1)
			for (let i = 0; i < ecdata[r].length; i++) {
				const modIndex = i + modPoly.getLength() - ecdata[r].length
				ecdata[r][i] = (modIndex >= 0) ? modPoly.get(modIndex) : 0
			}
		}
		let totalCodeCount = 0
		for (let i = 0; i < rsBlocks.length; i++) totalCodeCount += rsBlocks[i].totalCount
		const data = new Array(totalCodeCount)
		let index = 0
		for (let i = 0; i < maxDcCount; i++)
			for (let r = 0; r < rsBlocks.length; r++)
				if (i < dcdata[r].length) data[index++] = dcdata[r][i]
		for (let i = 0; i < maxEcCount; i++)
			for (let r = 0; r < rsBlocks.length; r++)
				if (i < ecdata[r].length) data[index++] = ecdata[r][i]
		return data
	}
}

/**
 * 自动检测最佳版本号
 */
function getBestTypeNumber(data, errorCorrectLevel) {
	for (let typeNumber = 1; typeNumber <= 10; typeNumber++) {
		try {
			const rsBlocks = getRsBlocks(typeNumber, errorCorrectLevel)
			let totalDataCount = 0
			for (let i = 0; i < rsBlocks.length; i++) totalDataCount += rsBlocks[i].dataCount
			const bitsNeeded = 4 + QRUtil.getLengthInBits(QRMode.MODE_8BIT_BYTE, typeNumber) + data.length * 8
			if (bitsNeeded <= totalDataCount * 8) return typeNumber
		} catch (e) { continue }
	}
	return 10
}

/**
 * 生成 QR Code 二维矩阵
 * @param {string} text - 要编码的文本（如钱包地址）
 * @param {number} errorCorrectLevel - 纠错等级，默认 M
 * @returns {{ modules: boolean[][], moduleCount: number }}
 */
function createQRCodeMatrix(text, errorCorrectLevel = QRErrorCorrectLevel.M) {
	const typeNumber = getBestTypeNumber(text, errorCorrectLevel)
	const qr = new QRCodeModel(typeNumber, errorCorrectLevel)
	qr.addData(text)
	qr.make()
	const moduleCount = qr.getModuleCount()
	const modules = []
	for (let row = 0; row < moduleCount; row++) {
		modules[row] = []
		for (let col = 0; col < moduleCount; col++) {
			modules[row][col] = qr.isDark(row, col)
		}
	}
	return { modules, moduleCount }
}

/**
 * 在 Canvas 上绘制 QR Code
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D 上下文
 * @param {string} text - 要编码的文本
 * @param {number} size - Canvas 尺寸 (px)
 * @param {object} options - 可选参数
 */
function drawQRCode(ctx, text, size, options = {}) {
	const { margin = 4, darkColor = '#000000', lightColor = '#ffffff', errorCorrectLevel = QRErrorCorrectLevel.M } = options
	const { modules, moduleCount } = createQRCodeMatrix(text, errorCorrectLevel)
	const cellSize = (size - margin * 2) / moduleCount

	// 背景
	ctx.setFillStyle ? ctx.setFillStyle(lightColor) : (ctx.fillStyle = lightColor)
	ctx.fillRect(0, 0, size, size)

	// 绘制模块
	ctx.setFillStyle ? ctx.setFillStyle(darkColor) : (ctx.fillStyle = darkColor)
	for (let row = 0; row < moduleCount; row++) {
		for (let col = 0; col < moduleCount; col++) {
			if (modules[row][col]) {
				const x = margin + col * cellSize
				const y = margin + row * cellSize
				ctx.fillRect(x, y, cellSize, cellSize)
			}
		}
	}
}

export { createQRCodeMatrix, drawQRCode, QRErrorCorrectLevel }
export default { createQRCodeMatrix, drawQRCode, QRErrorCorrectLevel }
